import { useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { UrlModalParam, UrlModalType } from "@rapid/constants/urlModal";

export interface UrlModalParams {
    open: string | null;
    resource: string | null;
}

export interface UseUrlModalResult {
    params: UrlModalParams;
    isOpen: boolean;
    close: () => void;
    openModal: (modalType: UrlModalType, resource?: string) => void;
}

export function useUrlModal(): UseUrlModalResult {
    const [searchParams, setSearchParams] = useSearchParams();

    const params = useMemo<UrlModalParams>(
        () => ({
            open: searchParams.get(UrlModalParam.Open),
            resource: searchParams.get(UrlModalParam.For)
        }),
        [searchParams]
    );

    const isOpen = params.open !== null;

    const close = useCallback(() => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete(UrlModalParam.Open);
        newParams.delete(UrlModalParam.For);
        setSearchParams(newParams, { replace: true });
    }, [searchParams, setSearchParams]);

    const openModal = useCallback(
        (modalType: UrlModalType, resource?: string) => {
            const newParams = new URLSearchParams(searchParams);
            newParams.set(UrlModalParam.Open, modalType);
            if (resource) {
                newParams.set(UrlModalParam.For, resource);
            } else {
                newParams.delete(UrlModalParam.For);
            }
            setSearchParams(newParams, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    return { params, isOpen, close, openModal };
}
